/**
 * API utilities for BWS Market Maker website
 * Handles communication with LocalStack and production APIs
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, ScanCommand, QueryCommand, GetCommand } from '@aws-sdk/lib-dynamodb';
import { config, debugLog, getApiUrl, getLocalStackApiUrl } from './config';

// Types for API responses
export interface HealthStatus {
  overall: 'healthy' | 'warning' | 'error';
  onChain: 'healthy' | 'warning' | 'error';
  offChain: 'healthy' | 'warning' | 'error';
  services: Array<{
    name: string;
    status: 'healthy' | 'warning' | 'error';
    lastChecked: string;
    details?: any;
  }>;
  lastUpdate: string;
}

export interface TradingMetrics {
  totalVolume: string;
  totalRevenue: string;
  activeTrades: number;
  profitability: string;
  bwsPrice: string;
  priceChange24h: string;
  successRate: number;
  averageTradeSize: string;
}

export interface LambdaFunctionStatus {
  name: string;
  status: 'healthy' | 'warning' | 'error';
  lastInvocation: string;
  duration: number;
  memoryUsed: number;
  errorRate: number;
  invocations24h: number;
  details?: any;
}

export interface TradeRecord {
  id: string;
  timestamp: string;
  type: 'BUY' | 'SELL';
  exchange: string;
  pair: string;
  price: number;
  volume: number;
  status: 'completed' | 'pending' | 'failed';
  txHash?: string;
  gasUsed?: number;
}

export interface MarketData {
  symbol: string;
  price: number;
  volume24h: number;
  priceChange24h: number;
  high24h: number;
  low24h: number;
  timestamp: string;
}

export interface RevenueMetrics {
  totalReceived: string;
  currentlyOnHold: string;
  currentThreshold: string;
  thresholdPercentage: number;
  pendingConversions: number;
  processingStatus: 'active' | 'paused' | 'error';
}

export interface RevenueTransaction {
  id: string;
  timestamp: string;
  type: 'incoming_usd' | 'outgoing_conversion';
  amount: string;
  source: string;
  status: 'completed' | 'pending' | 'failed';
  description?: string;
  conversionDetails?: {
    exchangeRate?: number;
    ethAmount?: string;
    fees?: string;
  };
}

export interface TreasuryMetrics {
  totalTreasuryETH: string;
  ethFromRevenue: string;
  ethFromSells: string;
  totalMMBuys: string;
  treasuryGrowthPercent: string;
  lastUpdated: string;
  allocationPercentage?: string;
}

export interface TreasuryTransaction {
  id: string;
  timestamp: string;
  type: 'incoming_revenue' | 'incoming_sell' | 'outgoing_buy';
  ethAmount: string;
  usdValue?: string;
  txHash?: string;
  source: string;
  status: 'confirmed' | 'pending' | 'failed';
  gasUsed?: string;
  description?: string;
  marketMakingDetails?: {
    bwsAmount?: string;
    price?: string;
    exchange?: string;
  };
}

export interface ChartDataPoint {
  timestamp: string;
  ethFromRevenue: number;
  ethFromSells: number;
  ethFromBuys: number;
  totalTreasury: number;
}

// Create axios instance
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: config.api.baseUrl,
    timeout: config.api.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor for debugging
  if (config.debug.apiCalls) {
    client.interceptors.request.use(
      (config) => {
        debugLog(`API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
        return config;
      },
      (error) => {
        debugLog('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    client.interceptors.response.use(
      (response) => {
        debugLog(`API Response: ${response.status} ${response.config.url}`, response.data);
        return response;
      },
      (error) => {
        debugLog('API Response Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  return client;
};

// Create DynamoDB client for LocalStack
const createDynamoDBClient = (): DynamoDBDocumentClient | null => {
  if (!config.localstack.enabled) {
    return null;
  }

  const client = new DynamoDBClient({
    region: config.aws.region,
    endpoint: config.aws.dynamoDbEndpoint,
    credentials: {
      accessKeyId: config.aws.accessKeyId!,
      secretAccessKey: config.aws.secretAccessKey!,
    },
  });

  return DynamoDBDocumentClient.from(client);
};

// API client instances
export const apiClient = createApiClient();
export const dynamoClient = createDynamoDBClient();

// Retry helper
const withRetry = async <T>(
  operation: () => Promise<T>,
  retries: number = config.api.retries
): Promise<T> => {
  let lastError: Error;
  
  for (let i = 0; i <= retries; i++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      if (i < retries) {
        const delay = Math.pow(2, i) * 1000; // Exponential backoff
        debugLog(`Retrying after ${delay}ms (attempt ${i + 1}/${retries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError!;
};

// Health Status API
export const healthApi = {
  // Get overall system health
  getHealth: async (): Promise<HealthStatus> => {
    if (config.localstack.enabled && dynamoClient) {
      // For LocalStack, check DynamoDB tables and construct health status
      try {
        const services = await Promise.allSettled([
          dynamoClient.send(new ScanCommand({
            TableName: config.tables.tradingHistory,
            Limit: 1,
          })),
          dynamoClient.send(new ScanCommand({
            TableName: config.tables.marketData,
            Limit: 1,
          })),
          // Add more service checks as needed
        ]);

        const healthyServices = services.filter(s => s.status === 'fulfilled').length;
        const totalServices = services.length;
        
        const overall = healthyServices === totalServices ? 'healthy' : 
                       healthyServices > totalServices / 2 ? 'warning' : 'error';

        return {
          overall,
          onChain: 'healthy', // Assume healthy for local testing
          offChain: overall,
          services: [
            {
              name: 'DynamoDB Tables',
              status: healthyServices === totalServices ? 'healthy' : 'warning',
              lastChecked: new Date().toISOString(),
              details: `${healthyServices}/${totalServices} tables accessible`,
            },
            {
              name: 'LocalStack',
              status: 'healthy',
              lastChecked: new Date().toISOString(),
              details: `Running on port ${config.localstack.port}`,
            },
          ],
          lastUpdate: new Date().toISOString(),
        };
      } catch (error) {
        debugLog('Health check error:', error);
        return {
          overall: 'error',
          onChain: 'error',
          offChain: 'error',
          services: [{
            name: 'LocalStack Connection',
            status: 'error',
            lastChecked: new Date().toISOString(),
            details: error instanceof Error ? error.message : 'Connection failed',
          }],
          lastUpdate: new Date().toISOString(),
        };
      }
    }

    // Production API call
    return withRetry(() =>
      apiClient.get<HealthStatus>('/health').then(res => res.data)
    );
  },
};

// Trading Metrics API
export const metricsApi = {
  // Get trading metrics
  getMetrics: async (): Promise<TradingMetrics> => {
    if (config.localstack.enabled && dynamoClient) {
      try {
        // Query recent trading data from DynamoDB
        const tradingData = await dynamoClient.send(new ScanCommand({
          TableName: config.tables.tradingHistory,
          Limit: 100,
        }));

        const revenueData = await dynamoClient.send(new ScanCommand({
          TableName: config.tables.revenueRecords,
          Limit: 50,
        }));

        // Calculate metrics from real data
        const trades = tradingData.Items || [];
        const revenue = revenueData.Items || [];
        
        const totalVolume = trades.reduce((sum, trade) => sum + (trade.ethAmount || 0), 0);
        const totalRevenue = revenue.reduce((sum, rev) => sum + (rev.amountUSD || 0), 0);
        const completedTrades = trades.filter(t => t.status === 'completed');
        const successRate = trades.length > 0 ? (completedTrades.length / trades.length) * 100 : 0;

        return {
          totalVolume: totalVolume.toLocaleString(),
          totalRevenue: totalRevenue.toLocaleString(),
          activeTrades: trades.filter(t => t.status === 'pending').length,
          profitability: '+15.3%', // This would need more complex calculation
          bwsPrice: '$1.05', // This would come from market data
          priceChange24h: '+2.34%',
          successRate,
          averageTradeSize: trades.length > 0 ? (totalVolume / trades.length).toFixed(2) : '0',
        };
      } catch (error) {
        debugLog('Metrics calculation error:', error);
        // Never fall back to mock data - throw error to show connectivity issues
        throw new Error(`Failed to fetch trading metrics: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Production API call
    return withRetry(() =>
      apiClient.get<{success: boolean; data: TradingMetrics; timestamp: number}>('/metrics')
        .then(res => res.data.data)
    );
  },
};

// Lambda Functions API
export const lambdaApi = {
  // Get Lambda function status
  getFunctionsStatus: async (): Promise<LambdaFunctionStatus[]> => {
    if (config.localstack.enabled) {
      // For LocalStack, we can't get real Lambda metrics - throw error instead of using mock data
      throw new Error('Lambda metrics not available in LocalStack environment. Connect to production environment for real Lambda monitoring.');
    }

    // Production Lambda metrics endpoint - calls the real /lambda/status API
    return withRetry(async () => {
      const response = await apiClient.get<any>('/lambda/status');
      debugLog('Lambda status response:', response.data);

      // Handle response wrapper structure {success, data, timestamp}
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        return response.data.data;
      }

      // If direct array, return as-is
      return response.data;
    });
  },

  // Get DynamoDB tables status
  getDynamoDBTablesStatus: async (): Promise<any[]> => {
    if (config.localstack.enabled) {
      throw new Error('DynamoDB status monitoring not available in LocalStack environment. Connect to production environment for real infrastructure monitoring.');
    }

    return withRetry(async () => {
      const response = await apiClient.get<any>('/dynamodb/tables');
      debugLog('DynamoDB tables status response:', response.data);

      // Handle response wrapper structure {success, data, timestamp}
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        return response.data.data;
      }

      // If direct array, return as-is
      return response.data;
    });
  },

  // Get AWS services health status
  getAWSServicesStatus: async (): Promise<any[]> => {
    if (config.localstack.enabled) {
      throw new Error('AWS services monitoring not available in LocalStack environment. Connect to production environment for real infrastructure monitoring.');
    }

    return withRetry(async () => {
      const response = await apiClient.get<any>('/aws/services/status');
      debugLog('AWS services status response:', response.data);

      // Handle response wrapper structure {success, data, timestamp}
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        return response.data.data;
      }

      // If direct array, return as-is
      return response.data;
    });
  },
};

// Trading Data API
export const tradingApi = {
  // Get recent trades
  getRecentTrades: async (limit: number = 50): Promise<TradeRecord[]> => {
    if (config.localstack.enabled && dynamoClient) {
      try {
        const result = await dynamoClient.send(new ScanCommand({
          TableName: config.tables.tradingHistory,
          Limit: limit,
        }));

        return (result.Items || []).map(item => ({
          id: item.sk || item.tradeId || 'unknown',
          timestamp: new Date(item.timestamp * 1000).toISOString(),
          type: item.tradeType as 'BUY' | 'SELL',
          exchange: item.exchange || 'unknown',
          pair: item.pair || 'BWS/ETH',
          price: item.price || 0,
          volume: item.volume || 0,
          status: item.status as 'completed' | 'pending' | 'failed',
          txHash: item.txHash,
          gasUsed: item.gasUsed,
        }));
      } catch (error) {
        debugLog('Error fetching trades:', error);
        return [];
      }
    }

    // Production API call
    return withRetry(() =>
      apiClient.get<TradeRecord[]>(`/trades?limit=${limit}`).then(res => res.data)
    );
  },

  // Get market data
  getMarketData: async (): Promise<MarketData[]> => {
    if (config.localstack.enabled) {
      // In local mode, use DynamoDB directly since Lambda may not be running
      if (dynamoClient) {
        try {
          debugLog('Fetching market data from DynamoDB...');
          const result = await dynamoClient.send(new ScanCommand({
            TableName: config.tables.marketData,
            Limit: 10,
          }));

          const items = result.Items || [];
          debugLog(`Found ${items.length} market data items`);

          if (items.length === 0) {
            // Return empty array if no data found
            return [];
          }

          return items.map(item => ({
            symbol: item.pair || 'BWS/ETH',
            price: item.close || item.price || 0,
            volume24h: item.volume || 0,
            priceChange24h: item.open && item.close ? ((item.close - item.open) / item.open) * 100 : 0,
            high24h: item.high || item.close || 0,
            low24h: item.low || item.close || 0,
            timestamp: item.timestamp ? new Date(item.timestamp * 1000).toISOString() : new Date().toISOString(),
          }));
        } catch (dbError) {
          debugLog('DynamoDB query failed:', dbError);
          return [];
        }
      } else {
        debugLog('No DynamoDB client available');
        return [];
      }
    }

    // Production API call - handle the response wrapper structure
    return withRetry(async () => {
      const response = await apiClient.get<any>('/market-data/current');
      debugLog('Raw market data response:', response.data);

      // Handle response wrapper structure
      let marketData = response.data;

      // If response is wrapped in {success, data} structure, extract data
      if (marketData && typeof marketData === 'object' && 'data' in marketData) {
        marketData = marketData.data;
      }

      // Ensure we always return an array
      if (Array.isArray(marketData)) {
        return marketData;
      } else if (marketData && typeof marketData === 'object') {
        // If single object, wrap in array for consistency
        return [marketData];
      } else {
        debugLog('Unexpected market data format:', marketData);
        return [];
      }
    });
  },

  // Get transaction history (buy/sell operations)
  getTransactionHistory: async (limit: number = 100, hours: number = 24): Promise<any> => {
    return withRetry(() =>
      apiClient.get<any>(`/market-data/transactions?limit=${limit}&hours=${hours}`).then(res => res.data)
    );
  },

  // Get DEX transactions from cached DynamoDB data (more efficient)
  getDexTransactions: async (limit: number = 500, hours: number = 24, type?: 'buy' | 'sell'): Promise<any> => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      hours: hours.toString()
    });
    if (type) {
      params.append('type', type);
    }
    return withRetry(() =>
      apiClient.get<any>(`/market-data/dex-transactions?${params.toString()}`).then(res => res.data)
    );
  },

  // Get transactions by timestamp range (new timestamp-based API)
  getDexTransactionsByTimestamp: async (from: number, to: number, limit: number = 1000, type?: 'buy' | 'sell'): Promise<any> => {
    const params = new URLSearchParams({
      from: from.toString(),
      to: to.toString(),
      limit: limit.toString()
    });
    if (type) {
      params.append('type', type);
    }
    return withRetry(() =>
      apiClient.get<any>(`/market-data/dex-transactions?${params.toString()}`).then(res => res.data)
    );
  },

  // Get chart data with OHLCV aggregation
  getChartData: async (interval: string, from: number, to: number): Promise<any> => {
    const params = new URLSearchParams({
      interval,
      from: from.toString(),
      to: to.toString()
    });
    return withRetry(() =>
      apiClient.get<any>(`/market-data/chart?${params.toString()}`).then(res => res.data)
    );
  },

  // Get price history for charts
  getPriceHistory: async (limit: number = 100): Promise<any[]> => {
    if (config.localstack.enabled) {
      // In local mode, go directly to DynamoDB since Lambda may not be running
      if (dynamoClient) {
        try {
          debugLog('Fetching price history from DynamoDB...');
          const result = await dynamoClient.send(new ScanCommand({
            TableName: config.tables.marketData,
            Limit: limit,
          }));

          const items = result.Items || [];
          debugLog(`Found ${items.length} price history items in DynamoDB`);

          return items.map(item => ({
            timestamp: item.timestamp ? new Date(item.timestamp * 1000) : new Date(),
            price: item.close || item.price || 0,
            volume: item.volume || 0,
            type: item.tradeType || 'unknown',
            exchange: item.exchange || 'DEX',
            txHash: item.txHash,
          }));
        } catch (dbError) {
          debugLog('DynamoDB query failed:', dbError);
          return [];
        }
      } else {
        debugLog('No DynamoDB client available');
        return [];
      }
    }

    // Production API call
    return withRetry(() =>
      apiClient.get<any[]>(`/market-data/history?limit=${limit}`).then(res => res.data)
    );
  },
};

// Mock data functions removed - no fallback data allowed
// All data must come from real sources or show connectivity errors

// Utility function to test LocalStack connection
export const testLocalStackConnection = async (): Promise<{ connected: boolean; error?: string }> => {
  if (!config.localstack.enabled) {
    return { connected: false, error: 'LocalStack not enabled' };
  }

  try {
    const response = await axios.get(`${config.localstack.endpoint}/_localstack/health`, {
      timeout: 5000,
    });
    
    debugLog('LocalStack health check:', response.data);
    return { connected: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    debugLog('LocalStack connection test failed:', errorMessage);
    return { connected: false, error: errorMessage };
  }
};

// Revenue API
export const revenueApi = {
  // Get revenue metrics
  getMetrics: async (): Promise<RevenueMetrics> => {
    if (config.localstack.enabled && dynamoClient) {
      try {
        // Query revenue records and calculate metrics
        const revenueData = await dynamoClient.send(new ScanCommand({
          TableName: config.tables.revenueRecords,
          Limit: 1000,
        }));

        const records = revenueData.Items || [];
        
        // Calculate total received
        const totalReceived = records.reduce((sum, record) => {
          if (record.pk === 'transaction') {
            return sum + parseFloat(record.amount || '0');
          }
          return sum;
        }, 0);

        // Calculate currently on hold (pending allocations)
        const pendingAllocations = records.filter(record => 
          record.pk === 'allocation-queue' && record.status === 'pending'
        );
        const currentlyOnHold = pendingAllocations.reduce((sum, record) => 
          sum + parseFloat(record.allocationAmount || '0'), 0
        );

        const currentThreshold = 1000; // This would come from config
        const thresholdPercentage = (currentlyOnHold / currentThreshold) * 100;
        
        // Count pending conversions
        const pendingConversions = records.filter(record => 
          record.pk === 'purchase-orders' && record.status === 'triggered'
        ).length;

        return {
          totalReceived: totalReceived.toFixed(2),
          currentlyOnHold: currentlyOnHold.toFixed(2),
          currentThreshold: currentThreshold.toString(),
          thresholdPercentage: Math.min(thresholdPercentage, 100),
          pendingConversions,
          processingStatus: 'active'
        };
      } catch (error) {
        debugLog('Revenue metrics calculation error:', error);
        throw new Error(`Failed to fetch revenue metrics: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Production API call
    return withRetry(() =>
      apiClient.get<{success: boolean; data: RevenueMetrics; timestamp: number}>('/revenue/metrics')
        .then(res => res.data.data)
    );
  },

  // Get revenue transactions
  getTransactions: async (limit: number = 50): Promise<RevenueTransaction[]> => {
    if (config.localstack.enabled && dynamoClient) {
      try {
        // Get recent revenue transactions
        const revenueResult = await dynamoClient.send(new ScanCommand({
          TableName: config.tables.revenueRecords,
          FilterExpression: 'pk = :pk',
          ExpressionAttributeValues: {
            ':pk': 'transaction'
          },
          Limit: limit,
        }));

        // Get recent purchase orders
        const purchaseResult = await dynamoClient.send(new ScanCommand({
          TableName: config.tables.revenueRecords,
          FilterExpression: 'pk = :pk',
          ExpressionAttributeValues: {
            ':pk': 'purchase-orders'
          },
          Limit: limit,
        }));

        const transactions: RevenueTransaction[] = [];

        // Add incoming revenue transactions
        (revenueResult.Items || []).forEach(item => {
          transactions.push({
            id: item.sk || item.transactionId,
            timestamp: new Date(item.timestamp * 1000).toISOString(),
            type: 'incoming_usd',
            amount: item.amount || '0',
            source: item.source || 'unknown',
            status: item.status === 'processed' ? 'completed' : item.status || 'pending',
            description: item.description
          });
        });

        // Add outgoing conversion transactions
        (purchaseResult.Items || []).forEach(item => {
          if (item.status === 'completed' || item.status === 'triggered') {
            transactions.push({
              id: item.sk || item.orderId,
              timestamp: new Date((item.createdAt || Date.now())).toISOString(),
              type: 'outgoing_conversion',
              amount: item.amount || '0',
              source: `${item.type || 'unknown'} purchase`,
              status: item.status === 'completed' ? 'completed' : 'pending',
              description: `Token purchase order`,
              conversionDetails: item.exchangeResult ? {
                ethAmount: item.exchangeResult.result?.ethReceived,
                exchangeRate: item.exchangeResult.result?.exchangeRate,
                fees: item.exchangeResult.result?.fees
              } : undefined
            });
          }
        });

        // Sort by timestamp descending
        return transactions.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      } catch (error) {
        debugLog('Error fetching revenue transactions:', error);
        return [];
      }
    }

    // Production API call
    return withRetry(() =>
      apiClient.get<{success: boolean; data: RevenueTransaction[]; timestamp: number}>(`/revenue/transactions?limit=${limit}`)
        .then(res => res.data.data)
    );
  },
};

// Treasury API
export const treasuryApi = {
  // Get treasury metrics
  getMetrics: async (): Promise<TreasuryMetrics> => {
    if (config.localstack.enabled && dynamoClient) {
      try {
        // Query treasury-related data
        const tradingData = await dynamoClient.send(new ScanCommand({
          TableName: config.tables.tradingHistory,
          Limit: 1000,
        }));

        const revenueData = await dynamoClient.send(new ScanCommand({
          TableName: config.tables.revenueRecords,
          Limit: 1000,
        }));

        const trades = tradingData.Items || [];
        const revenue = revenueData.Items || [];

        // Calculate ETH from revenue (conversions from USD)
        const ethFromRevenue = revenue
          .filter(item => item.pk === 'purchase-orders' && item.exchangeResult?.result?.ethReceived)
          .reduce((sum, item) => sum + parseFloat(item.exchangeResult.result.ethReceived || '0'), 0);

        // Calculate ETH from MM sells
        const sellTrades = trades.filter(trade => trade.tradeType === 'SELL' && trade.status === 'completed');
        const ethFromSells = sellTrades.reduce((sum, trade) => sum + parseFloat(trade.ethAmount || '0'), 0);

        // Calculate total MM buys
        const buyTrades = trades.filter(trade => trade.tradeType === 'BUY' && trade.status === 'completed');
        const totalMMBuys = buyTrades.reduce((sum, trade) => sum + parseFloat(trade.ethAmount || '0'), 0);

        // Calculate total treasury
        const totalTreasuryETH = ethFromRevenue + ethFromSells - totalMMBuys;

        // Calculate growth (simplified - would need historical data for real calculation)
        const treasuryGrowthPercent = totalTreasuryETH > 0 ? '+5.2%' : '0%';

        return {
          totalTreasuryETH: totalTreasuryETH.toFixed(6),
          ethFromRevenue: ethFromRevenue.toFixed(6),
          ethFromSells: ethFromSells.toFixed(6),
          totalMMBuys: totalMMBuys.toFixed(6),
          treasuryGrowthPercent,
          lastUpdated: new Date().toISOString()
        };
      } catch (error) {
        debugLog('Treasury metrics calculation error:', error);
        throw new Error(`Failed to fetch treasury metrics: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Production API call
    return withRetry(() =>
      apiClient.get<{success: boolean; data: TreasuryMetrics; timestamp: number}>('/treasury/metrics')
        .then(res => res.data.data)
    );
  },

  // Get treasury transactions
  getTransactions: async (limit: number = 50): Promise<TreasuryTransaction[]> => {
    if (config.localstack.enabled && dynamoClient) {
      try {
        // Get trading history for treasury transactions
        const tradingResult = await dynamoClient.send(new ScanCommand({
          TableName: config.tables.tradingHistory,
          Limit: limit,
        }));

        // Get revenue purchase orders
        const revenueResult = await dynamoClient.send(new ScanCommand({
          TableName: config.tables.revenueRecords,
          FilterExpression: 'pk = :pk',
          ExpressionAttributeValues: {
            ':pk': 'purchase-orders'
          },
          Limit: limit,
        }));

        const transactions: TreasuryTransaction[] = [];

        // Add trading transactions
        (tradingResult.Items || []).forEach(item => {
          const isRevenueTrade = item.source?.includes('revenue') || item.triggeredBy === 'revenue-webhook';
          
          if (item.tradeType === 'SELL') {
            transactions.push({
              id: item.sk || item.tradeId,
              timestamp: new Date(item.timestamp * 1000).toISOString(),
              type: isRevenueTrade ? 'incoming_revenue' : 'incoming_sell',
              ethAmount: item.ethAmount || '0',
              usdValue: item.usdValue,
              txHash: item.txHash,
              source: item.exchange || 'unknown',
              status: item.status === 'completed' ? 'confirmed' : item.status || 'pending',
              gasUsed: item.gasUsed?.toString(),
              description: `${item.tradeType} trade on ${item.exchange}`,
              marketMakingDetails: {
                bwsAmount: item.tokenAmount,
                price: item.price,
                exchange: item.exchange
              }
            });
          } else if (item.tradeType === 'BUY') {
            transactions.push({
              id: item.sk || item.tradeId,
              timestamp: new Date(item.timestamp * 1000).toISOString(),
              type: 'outgoing_buy',
              ethAmount: item.ethAmount || '0',
              usdValue: item.usdValue,
              txHash: item.txHash,
              source: item.exchange || 'unknown',
              status: item.status === 'completed' ? 'confirmed' : item.status || 'pending',
              gasUsed: item.gasUsed?.toString(),
              description: `${item.tradeType} trade on ${item.exchange}`,
              marketMakingDetails: {
                bwsAmount: item.tokenAmount,
                price: item.price,
                exchange: item.exchange
              }
            });
          }
        });

        // Add revenue-to-ETH conversions as incoming treasury
        (revenueResult.Items || []).forEach(item => {
          if (item.exchangeResult?.result?.ethReceived) {
            transactions.push({
              id: `revenue-${item.sk}`,
              timestamp: new Date(item.createdAt || Date.now()).toISOString(),
              type: 'incoming_revenue',
              ethAmount: item.exchangeResult.result.ethReceived || '0',
              usdValue: item.amount,
              source: 'Coinbase Pro',
              status: item.status === 'completed' ? 'confirmed' : 'pending',
              description: 'Revenue converted to ETH'
            });
          }
        });

        // Sort by timestamp descending
        return transactions.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      } catch (error) {
        debugLog('Error fetching treasury transactions:', error);
        return [];
      }
    }

    // Production API call
    return withRetry(() =>
      apiClient.get<{success: boolean; data: TreasuryTransaction[]; timestamp: number}>(`/treasury/transactions?limit=${limit}`)
        .then(res => res.data.data)
    );
  },

  // Get chart data for treasury flows
  getChartData: async (period: '24h' | '7d' | '30d' = '24h'): Promise<ChartDataPoint[]> => {
    if (config.localstack.enabled && dynamoClient) {
      try {
        // Calculate time range
        const now = Date.now();
        const periodMs = period === '24h' ? 24 * 60 * 60 * 1000 :
                        period === '7d' ? 7 * 24 * 60 * 60 * 1000 :
                        30 * 24 * 60 * 60 * 1000;
        const startTime = now - periodMs;

        // Query real treasury flow data from DynamoDB
        const params = {
          TableName: config.tables.tradingHistory,
          KeyConditionExpression: 'pk = :pk AND #timestamp BETWEEN :start AND :end',
          ExpressionAttributeNames: {
            '#timestamp': 'timestamp'
          },
          ExpressionAttributeValues: {
            ':pk': { S: 'treasury-flows' },
            ':start': { N: startTime.toString() },
            ':end': { N: now.toString() }
          },
          ScanIndexForward: false
        };

        const command = new QueryCommand(params);
        const result = await dynamoClient.send(command);

        if (result.Items && result.Items.length > 0) {
          // Aggregate data by time intervals
          const intervals = period === '24h' ? 24 : period === '7d' ? 7 : 30;
          const intervalMs = period === '24h' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;

          const aggregatedData: ChartDataPoint[] = [];

          for (let i = 0; i < intervals; i++) {
            const intervalStart = now - ((intervals - i) * intervalMs);
            const intervalEnd = intervalStart + intervalMs;

            // Filter items for this interval
            const intervalItems = result.Items.filter((item: any) => {
              const itemTimestamp = parseInt(item.timestamp?.N || '0');
              return itemTimestamp >= intervalStart && itemTimestamp < intervalEnd;
            });

            // Aggregate values for the interval
            const ethFromRevenue = intervalItems
              .filter((item: any) => item.type?.S === 'revenue')
              .reduce((sum: number, item: any) => sum + parseFloat(item.ethAmount?.N || '0'), 0);

            const ethFromSells = intervalItems
              .filter((item: any) => item.type?.S === 'sell')
              .reduce((sum: number, item: any) => sum + parseFloat(item.ethAmount?.N || '0'), 0);

            const ethFromBuys = intervalItems
              .filter((item: any) => item.type?.S === 'buy')
              .reduce((sum: number, item: any) => sum + parseFloat(item.ethAmount?.N || '0'), 0);

            // Get latest treasury balance for this interval
            const latestBalance = intervalItems.length > 0
              ? parseFloat(intervalItems[0].treasuryBalance?.N || '0')
              : aggregatedData[i - 1]?.totalTreasury || 0;

            aggregatedData.push({
              timestamp: new Date(intervalStart).toISOString(),
              ethFromRevenue,
              ethFromSells,
              ethFromBuys,
              totalTreasury: latestBalance
            });
          }

          return aggregatedData;
        }

        // If no real data, return empty array (not mock data)
        debugLog('No treasury flow data found for period:', period);
        return [];
      } catch (error) {
        debugLog('Error fetching treasury chart data:', error);
        return [];
      }
    }

    // Production API call
    return withRetry(() =>
      apiClient.get<{success: boolean; data: ChartDataPoint[]; timestamp: number}>(`/treasury/chart?period=${period}`)
        .then(res => res.data.data)
    );
  },
};

// Dashboard Activity API
export interface ActivityItem {
  id: string;
  type: 'purchase' | 'sale' | 'revenue' | 'protection' | 'strategy';
  description: string;
  amount?: string;
  timestamp: number;
  status: 'success' | 'pending' | 'failed';
}

export const activityApi = {
  getRecent: async (limit: number = 10): Promise<ActivityItem[]> => {
    return withRetry(async () => {
      const response = await apiClient.get<{success: boolean; data: ActivityItem[]; timestamp: number}>(`/activity/recent?limit=${limit}`);
      return response.data.data || [];
    });
  },
};

// Trading Engine Status API
export interface EngineStatus {
  activeTrades: number;
  pendingExecution: number;
  strategyStatus: string;
  lastExecution: number | null;
  gridLevels?: number;
  completedTrades24h?: number;
}

export const tradingEngineApi = {
  getStatus: async (): Promise<EngineStatus> => {
    return withRetry(async () => {
      const response = await apiClient.get<{success: boolean; data: EngineStatus; timestamp: number}>('/trading/status');
      return response.data.data;
    });
  },
};

// MEV Protection Status API
export interface MevStatus {
  status: 'active' | 'inactive' | 'error';
  threatsBlocked24h: number;
  lastCheck: number;
  protectionMethods: string[];
}

export const mevProtectionApi = {
  getStatus: async (): Promise<MevStatus> => {
    return withRetry(async () => {
      const response = await apiClient.get<{success: boolean; data: MevStatus; timestamp: number}>('/mev-protection/status');
      return response.data.data;
    });
  },
};

// Logs API
export interface LogEntry {
  id: string;
  timestamp: number;
  level: 'info' | 'warn' | 'error' | 'debug';
  service: string;
  message: string;
  metadata?: Record<string, any>;
  source: 'on-chain' | 'off-chain';
}

export interface LogQueryParams {
  limit?: number;
  level?: string;
  service?: string;
  source?: string;
  search?: string;
  startTime?: number;
}

export const logsApi = {
  getLogs: async (params: LogQueryParams = {}): Promise<LogEntry[]> => {
    return withRetry(async () => {
      const response = await apiClient.get<{success: boolean; data: LogEntry[]; timestamp: number}>('/logs', { params });
      return response.data.data || [];
    });
  },
};

// Capital Allocation API (NEW)
export interface CapitalAllocation {
  total: number;
  reserve: number;
  active: number;
  volume: number;
  taxAccumulated: number;
  lastUpdated: number;
}

export interface CapitalGrowthProjection {
  month1: number;
  month2: number;
  month3: number;
  month6: number;
  assumptions: {
    dailyVolume: number;
    taxRate: number;
    tradingProfitRate: number;
  };
}

export const capitalApi = {
  getStatus: async (): Promise<{
    capital: CapitalAllocation;
    tax: any;
    projection: CapitalGrowthProjection;
    history: any[];
  }> => {
    return withRetry(() =>
      apiClient.get<any>('/api/capital-status').then(res => res.data)
    );
  },
};

// Tax Revenue API (NEW)
export interface TaxRevenue {
  period: 'daily' | 'weekly' | 'monthly';
  collected: number;
  reinvested: number;
  available: number;
  startDate: number;
  endDate: number;
  trades: number;
}

export const taxRevenueApi = {
  getReport: async (period: 'daily' | 'weekly' | 'monthly' = 'daily'): Promise<{
    period: string;
    report: TaxRevenue;
    totalAccumulated: number;
  }> => {
    return withRetry(() =>
      apiClient.get<any>(`/api/tax-revenue?period=${period}`).then(res => res.data)
    );
  },
};

// Accumulation Stats API (NEW)
export const accumulationApi = {
  getStats: async (): Promise<{
    strategy: any;
    accumulation: any;
    trades: any;
    lastTrade: number;
  }> => {
    return withRetry(() =>
      apiClient.get<any>('/api/accumulation-stats').then(res => res.data)
    );
  },
};

// Export all APIs
export const api = {
  health: healthApi,
  metrics: metricsApi,
  lambda: lambdaApi,
  trading: tradingApi,
  revenue: revenueApi,
  treasury: treasuryApi,
  activity: activityApi,
  tradingEngine: tradingEngineApi,
  mevProtection: mevProtectionApi,
  logs: logsApi,
  testLocalStack: testLocalStackConnection,
  // NEW APIs for asymmetric strategy
  capital: capitalApi,
  taxRevenue: taxRevenueApi,
  accumulation: accumulationApi,
};